import React from "react"
import cn from "classnames"
import { useAppSelector } from "integrations/redux/hooks"
import { selectWeatherPreferences } from "features/map/control/selectors"
import { PrecipUnit } from "features/map/control/types"
import { ColorStop, colormapAt } from "../shared/colormap"
import {
    PRECIP_HEATMAP_FADE_IN_MAX_MM,
    PRECIP_HEATMAP_MAX_MM,
    PRECIP_RAIN_COLOR_STOPS,
} from "./PrecipitationHeatmapLayer"

const BASE_TICKS_MM = [0, 1, 2.5, 7.5, 15, 30, 50] as const

const UNIT_CONVERTERS: Record<PrecipUnit, (mm: number) => number> = {
    [PrecipUnit.Millimeters]: (v) => v,
    [PrecipUnit.Inches]: (v) => v / 25.4,
}

const cssRgba = (c: readonly [number, number, number], alpha: number) =>
    `rgba(${Math.round(c[0] * 255)}, ${Math.round(c[1] * 255)}, ${Math.round(c[2] * 255)}, ${alpha})`

const cssRgb = (c: readonly [number, number, number]) => cssRgba(c, 1)

const AXIS_MIN_MM = 0
const AXIS_MAX_MM = PRECIP_HEATMAP_MAX_MM
const AXIS_RANGE_MM = AXIS_MAX_MM - AXIS_MIN_MM

const pctOf = (mm: number) => (((mm - AXIS_MIN_MM) / AXIS_RANGE_MM) * 100).toFixed(2)

const gradientCss = (stops: ReadonlyArray<ColorStop>) => {
    const fadeBoundaryColor = colormapAt(PRECIP_HEATMAP_FADE_IN_MAX_MM, stops)
    const parts = [
        `${cssRgba(stops[0].color, 0)} 0%`,
        `${cssRgb(fadeBoundaryColor)} ${pctOf(PRECIP_HEATMAP_FADE_IN_MAX_MM)}%`,
        ...stops
            .filter((s) => s.value > PRECIP_HEATMAP_FADE_IN_MAX_MM)
            .map((s) => `${cssRgb(s.color)} ${pctOf(s.value)}%`),
    ]
    return parts.join(", ")
}

const rainGradientCss = gradientCss(PRECIP_RAIN_COLOR_STOPS)

function SnowGlyph() {
    return (
        <svg viewBox="0 0 12 12" className="h-3 w-3 flex-shrink-0" aria-hidden="true">
            <g stroke="currentColor" strokeWidth="1.3" strokeLinecap="round">
                <line x1="6" y1="1" x2="6" y2="11" />
                <line x1="1.67" y1="3.5" x2="10.33" y2="8.5" />
                <line x1="10.33" y1="3.5" x2="1.67" y2="8.5" />
            </g>
        </svg>
    )
}
function SleetGlyph() {
    return (
        <svg viewBox="0 0 12 12" className="h-3 w-3 flex-shrink-0" aria-hidden="true">
            <circle cx="6" cy="6" r="2.4" fill="currentColor" />
        </svg>
    )
}
function FreezingRainGlyph() {
    return (
        <svg viewBox="0 0 12 12" className="h-3 w-3 flex-shrink-0" aria-hidden="true">
            <circle cx="6" cy="6" r="3.2" fill="none" stroke="currentColor" strokeWidth="1.3" />
        </svg>
    )
}

const TYPE_GLYPHS = [
    { label: "Snow", Glyph: SnowGlyph },
    { label: "Sleet", Glyph: SleetGlyph },
    { label: "Freezing rain", Glyph: FreezingRainGlyph },
] as const

export const PrecipitationLegend = ({ isLight }: { isLight: boolean }) => {
    const { precipUnit } = useAppSelector(selectWeatherPreferences)
    const ticks = BASE_TICKS_MM.map((mm) => Number(UNIT_CONVERTERS[precipUnit](mm).toFixed(2)))
    const textColor = cn({ ["text-white"]: !isLight, ["text-black"]: isLight })
    return (
        <div className="flex flex-col gap-1.5">
            <div className={cn("flex items-center gap-3 text-[10px] leading-none", textColor)}>
                {TYPE_GLYPHS.map(({ label, Glyph }) => (
                    <span key={label} className="flex items-center gap-1">
                        <Glyph />
                        {label}
                    </span>
                ))}
            </div>
            <div className="flex items-end gap-2">
                <div>
                    <div
                        className={cn("h-2 w-[230px] rounded border", {
                            ["bg-gray-300 border-black-500"]: isLight,
                            ["bg-gray-600 border-white-500"]: !isLight,
                        })}
                        style={{ background: `linear-gradient(to right, ${rainGradientCss})` }}
                    />
                    <div className="mt-1 flex h-3 w-[230px] justify-between">
                        {ticks.map((v, i) => (
                            <span
                                key={i}
                                className={cn("font-mono text-[10px] leading-none", textColor)}
                            >
                                {v}
                            </span>
                        ))}
                    </div>
                </div>
                <span className={cn("whitespace-nowrap text-xs leading-none", textColor)}>
                    {precipUnit}/hr
                </span>
            </div>
        </div>
    )
}
